import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';
import certificateTemplateService from '@/services/certificate-template.service';
import {
  CertificateTemplateDto,
  CERTIFICATE_TEMPLATE_TYPE_LABELS
} from '@/types/certificate-template.types';
import { invalidateTemplateQueries } from './templateQueries';

interface UseTemplateConfirmModalProps {
  isOpen: boolean;
  template: Pick<CertificateTemplateDto, '_id' | 'template_name' | 'template_type'> | null;
  onClose: () => void;
  onSwitched?: () => void;
}

/** Asks before switching which template a certificate type uses (PATCH set-default). */
export default function UseTemplateConfirmModal({ isOpen, template, onClose, onSwitched }: UseTemplateConfirmModalProps) {
  const queryClient = useQueryClient();
  const typeLabel = template ? CERTIFICATE_TEMPLATE_TYPE_LABELS[template.template_type] : '';

  // Same lookup the backend uses when issuing (default + active, else first active)
  const { data: current, isLoading: isLoadingCurrent, isError: currentFailed } = useQuery({
    queryKey: ['certificateTemplates', 'in-use', template?.template_type],
    queryFn: () => certificateTemplateService.getTemplateByType(template!.template_type),
    enabled: isOpen && !!template,
    // The list page just loaded this; don't fetch it again on open
    staleTime: 30_000
  });

  const switchMutation = useMutation({
    mutationFn: (id: string) => certificateTemplateService.setAsDefault(id),
    onSuccess: () => {
      toast.success(`New ${typeLabel} certificates will now use "${template?.template_name}".`);
      // Only this type's default changed; other types and the trash count stay as they are
      invalidateTemplateQueries(queryClient, template?.template_type, { inUse: true, deletedCount: false });
      // Mark stale only: refetching an open editor would reload its canvas and lose undo history
      queryClient.invalidateQueries({ queryKey: ['certificateTemplate', template?._id], refetchType: 'none' });
      onSwitched?.();
      onClose();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to switch template');
    }
  });

  if (!template) return null;

  const isSaving = switchMutation.isPending;
  const alreadyInUse = current?._id === template._id;
  const handleClose = () => { if (!isSaving) onClose(); };

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={handleClose}
        disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {current && !alreadyInUse ? 'Keep current' : 'Not now'}
      </button>
      <button
        type="button"
        onClick={() => switchMutation.mutate(template._id)}
        disabled={isSaving || isLoadingCurrent || alreadyInUse}
        className="px-4 py-2 text-sm font-semibold text-white bg-brand-green-600 rounded-lg hover:bg-brand-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
        {current && !alreadyInUse ? 'Use new template' : 'Use this template'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Use this template for ${typeLabel} certificates?`}
      size="md"
      footer={footer}
      closeOnOverlayClick={!isSaving}
    >
      {isLoadingCurrent ? (
        <div className="flex items-center justify-center py-6 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking the current template...
        </div>
      ) : alreadyInUse ? (
        <p className="text-sm text-gray-700">
          "{template.template_name}" is already the default template for {typeLabel} certificates.
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0 p-3 rounded-lg border border-gray-200 bg-gray-50">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Default now</p>
              <p className="text-sm font-semibold text-gray-900 break-words mt-1" title={current?.template_name}>
                {current?.template_name || (currentFailed ? 'Could not check' : 'No default yet')}
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="flex-1 min-w-0 p-3 rounded-lg border border-green-300 bg-green-50">
              <p className="text-xs font-medium text-green-700 uppercase tracking-wide">New</p>
              <p className="text-sm font-semibold text-gray-900 break-words mt-1" title={template.template_name}>
                {template.template_name}
              </p>
            </div>
          </div>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span>From now on, new {typeLabel} certificates will use the template you pick.</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Certificates already issued will not change. Courses, learning paths and certifications that chose their own template keep using it.</span>
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span>The other template is kept. You can switch back any time from the templates list.</span>
            </li>
          </ul>
        </div>
      )}
    </Modal>
  );
}
